import { useState, useEffect } from 'react';
import { calculateFourPillars, calculateElementBalance, analyzeElements } from '../../../utils/fourPillars';

interface FormData {
  name: string;
  birthDate: string;
  birthTime: string;
  birthPlace: string;
  gender: 'male' | 'female';
}

interface ResultsPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  formData: FormData;
  onPayClick: () => void;
}

const ELEMENT_LABELS: Record<string, { label: string; color: string; barColor: string }> = {
  wood: { label: '목(木)', color: 'text-emerald-400', barColor: '#34d399' },
  fire: { label: '화(火)', color: 'text-red-400', barColor: '#f87171' },
  earth: { label: '토(土)', color: 'text-yellow-400', barColor: '#facc15' },
  metal: { label: '금(金)', color: 'text-gray-300', barColor: '#d1d5db' },
  water: { label: '수(水)', color: 'text-cyan-400', barColor: '#22d3ee' },
};

const PILLAR_LABELS = ['년주(年柱)', '월주(月柱)', '일주(日柱)', '시주(時柱)'];

const LOADING_STEPS = [
  '사주팔자 기둥 산출 중...',
  '오행 균형 분석 중...',
  '십신 관계 계산 중...',
  'AI 운명 패턴 해석 중...',
  '전생 인연 탐색 중...',
];

export interface PlanInfo {
  id: 'basic' | 'premium' | 'vip';
  name: string;
  nameKr: string;
  price: number;
  originalPrice: number | null;
  desc: string;
  features: string[];
  isLaunch?: boolean;
}

const PLANS: PlanInfo[] = [
  {
    id: 'basic',
    name: 'Basic',
    nameKr: '베이직',
    price: 9.99,
    originalPrice: null,
    desc: '오늘의 운세 + 성향 분석',
    features: ['일일 운세 리포트', '기본 성향 분석', '오행 요약'],
  },
  {
    id: 'premium',
    name: 'Premium',
    nameKr: '프리미엄',
    price: 29.99,
    originalPrice: 59,
    desc: '사주 · 점성술 · 주역 통합 인생 리포트',
    features: ['사주팔자 완전 분석', '대운 + 2026 월별 운세', '서양 점성술 통합', '주역 해석'],
    isLaunch: true,
  },
  {
    id: 'vip',
    name: 'VIP',
    nameKr: 'VIP',
    price: 99.00,
    originalPrice: null,
    desc: '3종 복합 엔진 + AI 1:1 상담',
    features: ['사주 + 점성술 + 주역 3중 분석', 'AI 1:1 심층 채팅 무제한', '프리미엄 전체 포함'],
  },
];

export default function ResultsPreviewModal({
  isOpen,
  onClose,
  formData,
  onPayClick,
}: ResultsPreviewModalProps) {
  const [phase, setPhase] = useState<'loading' | 'results'>('loading');
  const [loadingStep, setLoadingStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [selectedPlanId, setSelectedPlanId] = useState<'basic' | 'premium' | 'vip'>('premium');

  useEffect(() => {
    if (!isOpen) {
      setPhase('loading');
      setLoadingStep(0);
      setProgress(0);
      return;
    }
    let p = 0;
    const interval = setInterval(() => {
      p += 2;
      setProgress(p);
      if (p % 20 === 0) setLoadingStep((s) => Math.min(s + 1, LOADING_STEPS.length - 1));
      if (p >= 100) { clearInterval(interval); setTimeout(() => setPhase('results'), 300); }
    }, 55);
    return () => clearInterval(interval);
  }, [isOpen]);

  if (!isOpen) return null;

  const fp = (formData.birthDate && formData.birthTime)
    ? calculateFourPillars(formData.birthDate, formData.birthTime) : null;
  const balance = fp ? calculateElementBalance(fp) : null;
  const analysis = balance ? analyzeElements(balance) : null;
  const totalElements = balance ? Object.values(balance).reduce((a, b) => a + b, 0) || 1 : 1;
  const pillars = fp ? [fp.year, fp.month, fp.day, fp.hour] : [];
  const birthYear = formData.birthDate ? new Date(formData.birthDate).getFullYear() : 0;
  const age = birthYear ? new Date().getFullYear() - birthYear : 0;

  const selectedPlan = PLANS.find((p) => p.id === selectedPlanId)!;

  const handlePayClick = () => {
    localStorage.setItem('destiny-selected-plan', JSON.stringify(selectedPlan));
    onPayClick();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/85 backdrop-blur-md"
        onClick={phase === 'results' ? onClose : undefined} />

      <div className="relative w-full max-w-lg max-h-[90vh] overflow-hidden rounded-3xl"
        style={{
          background: 'linear-gradient(135deg, #0d0820 0%, #0a0618 50%, #0d0820 100%)',
          border: '1px solid rgba(251,191,36,0.3)',
          boxShadow: '0 0 80px rgba(139,92,246,0.3), 0 0 0 1px rgba(255,255,255,0.05) inset',
        }}
      >
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-primary-gold to-transparent" />

        {phase === 'results' && (
          <button onClick={onClose}
            className="absolute top-4 right-4 z-20 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center text-white/60 hover:text-white transition-all cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        )}

        {/* ── LOADING ── */}
        {phase === 'loading' && (
          <div className="p-10 flex flex-col items-center justify-center min-h-[420px]">
            <div className="relative w-24 h-24 mb-8">
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-primary-gold/30 to-purple-600/30 blur-2xl animate-pulse" />
              <div className="absolute inset-0 rounded-full border-2 border-primary-gold/30 animate-spin" style={{ animationDuration: '3s' }} />
              <div className="absolute inset-2 rounded-full border border-purple-400/20 animate-spin" style={{ animationDuration: '2s', animationDirection: 'reverse' }} />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-4xl text-primary-gold" style={{ fontFamily: '"Noto Serif KR", serif', textShadow: '0 0 20px rgba(251,191,36,0.8)' }}>命</span>
              </div>
            </div>
            <h3 className="text-xl font-bold text-white mb-2">AI 운명 분석 중</h3>
            <p className="text-purple-300/70 text-sm mb-8 text-center">
              {formData.name ? `${formData.name}님의 ` : ''}사주팔자를 분석하고 있습니다
            </p>
            <div className="w-full max-w-xs mb-4">
              <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-primary-gold via-yellow-300 to-primary-gold transition-all duration-100"
                  style={{ width: `${progress}%` }} />
              </div>
              <div className="flex justify-between mt-1.5">
                <span className="text-xs text-purple-400/60">{LOADING_STEPS[loadingStep]}</span>
                <span className="text-xs text-primary-gold/80">{progress}%</span>
              </div>
            </div>
            <div className="space-y-2 w-full max-w-xs">
              {LOADING_STEPS.slice(0, loadingStep + 1).map((step, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${i < loadingStep ? 'bg-primary-gold' : 'border-2 border-primary-gold/50 animate-pulse'}`}>
                    {i < loadingStep && <i className="ri-check-line text-[10px] text-black"></i>}
                  </div>
                  <span className={`text-xs ${i < loadingStep ? 'text-primary-gold/70 line-through' : 'text-purple-300/80'}`}>{step}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── RESULTS ── */}
        {phase === 'results' && fp && balance && analysis && (
          <div className="relative flex flex-col max-h-[90vh]">
            {/* Header */}
            <div className="px-7 pt-7 pb-4 flex-shrink-0">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-1.5 h-1.5 rounded-full bg-primary-gold animate-pulse" />
                <span className="text-[10px] tracking-widest text-primary-gold/70 uppercase">AI 운명 분석 결과</span>
              </div>
              <h2 className="text-2xl font-bold text-white">
                {formData.name ? `${formData.name}님의 ` : ''}운명 리포트
              </h2>
              <p className="text-xs text-purple-300/70 mt-0.5">
                {formData.birthDate} · {formData.birthTime} · {formData.birthPlace}
                {age > 0 && ` · 만 ${age}세`}
              </p>
            </div>

            {/* Scrollable */}
            <div className="overflow-y-auto flex-1" style={{ maxHeight: 'calc(90vh - 340px)' }}>
              <div className="px-7 pb-3 space-y-4">
                {/* 사주 기둥 */}
                <div>
                  <h4 className="text-xs font-semibold text-primary-gold/80 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <i className="ri-ancient-gate-line"></i> 사주팔자 (四柱八字)
                  </h4>
                  <div className="grid grid-cols-4 gap-2">
                    {pillars.map((pillar, i) => (
                      <div key={i} className="text-center rounded-2xl p-3"
                        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(251,191,36,0.15)' }}
                      >
                        <p className="text-[10px] text-purple-400/70 mb-2">{PILLAR_LABELS[i]}</p>
                        <div className="text-2xl font-bold text-primary-gold leading-none" style={{ fontFamily: '"Noto Serif KR", serif' }}>{pillar.stem}</div>
                        <div className="w-px h-3 bg-primary-gold/20 mx-auto my-1" />
                        <div className="text-2xl font-bold text-purple-300 leading-none" style={{ fontFamily: '"Noto Serif KR", serif' }}>{pillar.branch}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 오행 균형 */}
                <div>
                  <h4 className="text-xs font-semibold text-primary-gold/80 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <i className="ri-pie-chart-line"></i> 오행 균형
                  </h4>
                  <div className="space-y-2">
                    {(Object.entries(balance) as [string, number][]).map(([key, val]) => {
                      const pct = Math.round((val / totalElements) * 100);
                      const meta = ELEMENT_LABELS[key];
                      return (
                        <div key={key} className="flex items-center gap-3">
                          <span className={`text-xs font-semibold w-14 flex-shrink-0 ${meta.color}`}>{meta.label}</span>
                          <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
                            <div className="h-full rounded-full transition-all duration-700"
                              style={{ width: `${pct}%`, backgroundColor: meta.barColor, opacity: 0.8 }} />
                          </div>
                          <span className="text-xs text-white/50 w-6 text-right">{val}</span>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-xs text-purple-300/70 mt-2 leading-relaxed">
                    <span className="text-primary-gold font-semibold">{analysis.strongest}</span>이 가장 강합니다.
                  </p>
                </div>

                {/* Blurred section + Paywall */}
                <div className="relative">
                  <div className="space-y-4 select-none" style={{ filter: 'blur(5px)', pointerEvents: 'none' }}>
                    <div>
                      <h4 className="text-xs font-semibold text-primary-gold/80 uppercase tracking-wider mb-2">상세 운명 해석</h4>
                      <p className="text-sm text-purple-200/80 leading-relaxed">일주 분석에 따르면 이 사람은 매우 특별한 운명의 패턴을 가지고 있습니다. 재성이 강하게 작용하여 30대 중반 이후 큰 재물운이 열리며...</p>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {['재물운 ★★★★☆', '연애운 ★★★☆☆', '건강운 ★★★★★'].map((item) => (
                        <div key={item} className="rounded-xl p-3 bg-white/5 text-center"><p className="text-xs text-white/70">{item}</p></div>
                      ))}
                    </div>
                  </div>
                  <div className="absolute inset-0 flex flex-col items-center justify-end rounded-2xl pb-2"
                    style={{ background: 'linear-gradient(to bottom, transparent 0%, rgba(10,6,24,0.88) 30%, rgba(10,6,24,0.98) 100%)' }}
                  >
                    <div className="text-center px-4 pt-10">
                      <div className="mb-3 max-w-xs mx-auto rounded-2xl px-4 py-3"
                        style={{ background: 'rgba(251,191,36,0.07)', border: '1px solid rgba(251,191,36,0.2)' }}
                      >
                        <p className="text-[9px] tracking-[0.25em] text-primary-gold/70 uppercase font-semibold mb-1.5">DESTINY GPT PREMIUM</p>
                        <p className="text-white/85 text-xs leading-relaxed font-medium">
                          "We bridge the gap between <span className="text-primary-gold">Western Astrology</span> and <span className="text-purple-300">Eastern metaphysics</span> (Saju &amp; I-Ching) to deliver a high-fidelity destiny roadmap."
                        </p>
                        <p className="text-primary-gold font-bold text-xs mt-1.5 tracking-wide">Stop guessing and start ascending.</p>
                      </div>
                      <div className="relative w-10 h-10 mx-auto mb-2">
                        <div className="absolute inset-0 rounded-full bg-primary-gold/20 blur-xl animate-pulse" />
                        <div className="relative w-full h-full rounded-full bg-gradient-to-br from-amber-400 to-yellow-600 flex items-center justify-center">
                          <i className="ri-lock-2-fill text-lg text-black"></i>
                        </div>
                      </div>
                      <p className="text-white font-bold text-sm mb-0.5">전체 결과 잠금 해제</p>
                      <p className="text-purple-300/60 text-[11px]">플랜을 선택하고 전체 리포트를 확인하세요</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── CTA FOOTER — Tiered Pricing ── */}
            <div className="px-5 py-4 flex-shrink-0"
              style={{ borderTop: '1px solid rgba(251,191,36,0.15)', background: 'rgba(8,4,20,0.98)' }}
            >
              {/* Plan Cards */}
              <div className="grid grid-cols-3 gap-2 mb-3">
                {PLANS.map((plan) => {
                  const isSelected = selectedPlanId === plan.id;
                  const isPremium = plan.id === 'premium';
                  const isVip = plan.id === 'vip';
                  return (
                    <button
                      key={plan.id}
                      onClick={() => setSelectedPlanId(plan.id)}
                      className={`relative flex flex-col items-center rounded-2xl p-3 transition-all duration-200 cursor-pointer text-left ${
                        isSelected
                          ? isPremium
                            ? 'ring-2 ring-primary-gold scale-[1.03]'
                            : isVip
                            ? 'ring-2 ring-purple-400 scale-[1.03]'
                            : 'ring-2 ring-white/30 scale-[1.02]'
                          : 'opacity-70 hover:opacity-90'
                      }`}
                      style={{
                        background: isSelected
                          ? isPremium
                            ? 'linear-gradient(135deg, rgba(251,191,36,0.18) 0%, rgba(251,191,36,0.08) 100%)'
                            : isVip
                            ? 'linear-gradient(135deg, rgba(139,92,246,0.22) 0%, rgba(139,92,246,0.1) 100%)'
                            : 'rgba(255,255,255,0.08)'
                          : isPremium
                          ? 'rgba(251,191,36,0.05)'
                          : isVip
                          ? 'rgba(139,92,246,0.06)'
                          : 'rgba(255,255,255,0.04)',
                        border: isSelected
                          ? isPremium
                            ? '1px solid rgba(251,191,36,0.6)'
                            : isVip
                            ? '1px solid rgba(139,92,246,0.6)'
                            : '1px solid rgba(255,255,255,0.3)'
                          : isPremium
                          ? '1px solid rgba(251,191,36,0.2)'
                          : isVip
                          ? '1px solid rgba(139,92,246,0.2)'
                          : '1px solid rgba(255,255,255,0.1)',
                      }}
                    >
                      {/* Badge */}
                      {isPremium && (
                        <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full text-[9px] font-black text-black whitespace-nowrap"
                          style={{ background: 'linear-gradient(90deg, #fbbf24, #f59e0b)' }}
                        >
                          추천!
                        </div>
                      )}
                      {isVip && (
                        <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full text-[9px] font-black text-white whitespace-nowrap"
                          style={{ background: 'linear-gradient(90deg, #7c3aed, #a855f7)' }}
                        >
                          ELITE
                        </div>
                      )}

                      {/* Plan name */}
                      <span className={`text-[11px] font-black mb-1 ${isPremium ? 'text-primary-gold' : isVip ? 'text-purple-300' : 'text-white/70'}`}>
                        {plan.name}
                      </span>

                      {/* Price */}
                      <div className="text-center">
                        {plan.originalPrice && (
                          <p className="text-[10px] line-through text-white/35 leading-none">${plan.originalPrice}</p>
                        )}
                        <p className={`text-base font-black leading-tight ${isPremium ? 'text-primary-gold' : isVip ? 'text-purple-300' : 'text-white'}`}>
                          ${plan.price}
                        </p>
                      </div>

                      {/* Launch badge */}
                      {plan.isLaunch && (
                        <div className="mt-1 px-1.5 py-0.5 rounded-full text-[8px] font-bold text-black whitespace-nowrap"
                          style={{ background: '#fbbf24' }}
                        >
                          🚀 런칭특가
                        </div>
                      )}

                      {/* Desc */}
                      <p className="text-[9px] text-white/50 text-center mt-1 leading-tight">{plan.desc}</p>

                      {/* Selected indicator */}
                      {isSelected && (
                        <div className={`absolute bottom-2 right-2 w-3.5 h-3.5 rounded-full flex items-center justify-center ${isPremium ? 'bg-primary-gold' : isVip ? 'bg-purple-400' : 'bg-white/60'}`}>
                          <i className="ri-check-line text-[8px] text-black"></i>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Selected plan detail */}
              {selectedPlan.id === 'premium' && (
                <div className="mb-3 flex items-center justify-center gap-2 text-[10px]">
                  <span className="text-white/40 line-through">${selectedPlan.originalPrice} 정가</span>
                  <i className="ri-arrow-right-line text-primary-gold/60 text-[9px]"></i>
                  <span className="text-primary-gold font-bold">${selectedPlan.price} 런칭 기념가</span>
                  <span className="px-1.5 py-0.5 rounded-full text-[8px] font-bold text-black bg-rose-400">
                    {Math.round((1 - selectedPlan.price / selectedPlan.originalPrice!) * 100)}% OFF
                  </span>
                </div>
              )}

              {/* CTA Button */}
              <button
                onClick={handlePayClick}
                className={`w-full py-3 rounded-xl font-bold text-sm transition-all hover:scale-[1.02] cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap ${
                  selectedPlanId === 'vip'
                    ? 'bg-gradient-to-r from-purple-500 to-violet-600 text-white'
                    : 'bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-600 text-black'
                }`}
                style={{
                  boxShadow: selectedPlanId === 'vip'
                    ? '0 0 25px rgba(139,92,246,0.35)'
                    : '0 0 25px rgba(251,191,36,0.35)',
                }}
              >
                <i className="ri-secure-payment-fill"></i>
                {selectedPlan.nameKr} 플랜 결제 — ${selectedPlan.price}
                <i className="ri-arrow-right-line"></i>
              </button>

              <div className="flex items-center justify-center gap-4 mt-2">
                {[
                  { icon: 'ri-shield-check-fill', color: 'text-emerald-400', label: 'SSL 보안' },
                  { icon: 'ri-lock-fill', color: 'text-primary-gold/60', label: '암호화' },
                  { icon: 'ri-refund-2-line', color: 'text-purple-400/60', label: '즉시 발급' },
                ].map((b) => (
                  <div key={b.label} className="flex items-center gap-1">
                    <i className={`${b.icon} ${b.color} text-xs`}></i>
                    <span className="text-[10px] text-purple-400/50">{b.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
